import { useState } from "react";
import { motion } from "framer-motion";
import { ShieldAlert, Repeat, Sparkles, ChevronRight, Check } from "lucide-react";
import { ExerciseSheet } from "./ExerciseSheet";
import { PROFILE_META } from "../config/program";
import {
  getExtDay, getExercise, distinctExercises, fullSequence,
  LEVEL_DOSAGE, SCARICO_DAYS, SCARICO_NOTE, SEQUENCE_FRAME,
} from "../config/program20";

const CHOICE_KEY = "lf_choice";
const readChoice = () => {
  try { return JSON.parse(localStorage.getItem(CHOICE_KEY)); } catch { return null; }
};

export const ExtDay = ({ profile, level, dayNum, navigate }) => {
  const data = getExtDay(profile, dayNum);
  const meta = PROFILE_META[profile];
  const isScarico = SCARICO_DAYS.includes(dayNum);
  const [sheetEx, setSheetEx] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [choice, setChoice] = useState(() => readChoice() || []);

  const open = (name) => { setSheetEx(getExercise(name)); setSheetOpen(true); };

  // Sequenza da mostrare
  let seq = data.resolvedSeq;
  if (data.special === "choose") {
    seq = choice;
  } else if (data.special === "personal") {
    const saved = readChoice();
    const base = saved && saved.length === 4 ? saved : fullSequence(profile).slice(0, 4);
    seq = [...base, data.extra];
  }

  const options = data.special === "choose" ? distinctExercises(profile) : null;
  const toggleChoice = (name) => {
    setChoice((prev) => {
      let next;
      if (prev.includes(name)) next = prev.filter((n) => n !== name);
      else if (prev.length >= 4) return prev; // blocca la quinta
      else next = [...prev, name];
      localStorage.setItem(CHOICE_KEY, JSON.stringify(next));
      return next;
    });
  };
  const q = data.special === "choose" ? `?p=${profile}&l=${level}` : `?p=${profile}&l=${level}`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
      className="flex flex-1 flex-col pt-1" data-testid={`day-page-${dayNum}`}
    >
      {/* Avviso di sicurezza in cima */}
      <div className="flex gap-3 rounded-2xl border-l-4 border-coral bg-coral/10 p-4" data-testid="day-safety">
        <ShieldAlert size={20} className="mt-0.5 shrink-0 text-coral" />
        <div className="text-sm leading-relaxed text-ink/80">
          {meta.safety}
          {level === "Alto" && <span className="mt-1 block font-medium text-ink">{meta.extra_alto}</span>}
        </div>
      </div>

      <p className="mt-5 text-xs font-semibold uppercase tracking-wider text-coral">Giorno {dayNum} · {data.min} min</p>
      <h2 className="mt-1 font-serif text-3xl font-bold leading-tight text-ink">{data.title}</h2>
      <span className="mt-3 inline-flex w-fit items-center rounded-full bg-ink/10 px-3 py-1.5 text-xs font-medium text-ink/70" data-testid="day-level">
        {meta.name} · {level}
      </span>
      <p className="mt-4 text-sm leading-relaxed text-ink/75">{data.intro}</p>

      {/* Sequenza */}
      <div className="mt-6">
        <div className="mb-2 flex items-center gap-2">
          <Repeat size={18} className="text-coral" />
          <h3 className="font-serif text-lg font-semibold text-ink">Sequenza</h3>
        </div>
        <div className="mb-3 rounded-xl bg-coral/10 px-4 py-3 text-sm text-ink/80" data-testid="dosage-note">
          {isScarico ? SCARICO_NOTE : `Livello ${level}: ${LEVEL_DOSAGE[level]}`}
          <span className="mt-1 block text-xs text-ink/55">{SEQUENCE_FRAME}</span>
        </div>

        {data.special === "choose" ? (
          <div data-testid="choice-list">
            <p className="mb-2 text-sm text-ink/70">Scegli esattamente 4 esercizi ({choice.length}/4):</p>
            <div className="flex flex-col gap-2">
              {options.map((name) => {
                const sel = choice.includes(name);
                return (
                  <button key={name} type="button" onClick={() => toggleChoice(name)}
                    data-testid={`choice-${name}`}
                    className={`flex items-center justify-between rounded-xl border-2 px-4 py-3 text-left text-sm font-medium transition-colors ${
                      sel ? "border-coral bg-coral-soft text-ink" : "border-transparent bg-white/80 text-ink/80"
                    }`}>
                    {name}
                    <span className={`flex h-5 w-5 items-center justify-center rounded border-2 ${sel ? "border-coral bg-coral text-white" : "border-ink/25 text-transparent"}`}>
                      <Check size={12} strokeWidth={3} />
                    </span>
                  </button>
                );
              })}
            </div>
            {choice.length === 4 && (
              <p className="mt-3 text-sm font-medium text-coral" data-testid="choice-saved">La tua sequenza è salvata su questo dispositivo.</p>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {seq.map((name, i) => (
              <button key={i} type="button" onClick={() => open(name)} data-testid={`routine-item-${dayNum}-${i}`}
                className="flex items-center justify-between rounded-xl bg-white/80 px-4 py-3 text-left shadow-sm transition-colors hover:bg-coral-soft">
                <span className="text-sm font-semibold text-ink">{name}</span>
                <ChevronRight size={18} className="shrink-0 text-coral" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Gesto del giorno */}
      <div className="mt-6">
        <div className="mb-2 flex items-center gap-2">
          <Sparkles size={18} className="text-coral" />
          <h3 className="font-serif text-lg font-semibold text-ink">Gesto del giorno</h3>
        </div>
        <p className="text-sm leading-relaxed text-ink/80">{data.gesto} <span className="text-ink/50">(1 minuto)</span></p>
      </div>

      <button type="button" onClick={() => navigate(`/checkin/${dayNum}${q}`)} data-testid="open-checkin-button"
        className="mt-7 w-full rounded-2xl bg-coral px-6 py-4 text-base font-semibold text-white shadow-md shadow-coral/30 transition-all hover:-translate-y-0.5">
        Apri il check-in
      </button>

      <ExerciseSheet exercise={sheetEx} open={sheetOpen} onOpenChange={setSheetOpen} />
    </motion.div>
  );
};
